import { TagModalService } from '@app/tags/services/tag-modal.service';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  Inject,
  signal,
} from '@angular/core';
import { TypeaheadSearchFunction } from '@app/shared/shared-api.model';
import { UserSearchV3Component } from '@app/user/components/user-search-v3/user-search-v3.component';
import { UserProfileSummary, UserSearchItem } from '@app/user/user-api.model';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { UserService } from '@app/user/services/user.service';
import { AuthService, GroupService } from '@dg/shared-services';
import { Group, GroupPrivacyLevel } from '@app/groups/group-api';
import { OrgMembersService } from '@app/orgs/services/org-members.service';
import {
  AlertComponent,
  DialogService,
  TextareaComponent,
} from '@degreed/apollo-angular';
import { DIALOG_DATA } from '@angular/cdk/dialog';
import { TagsApi } from '@app/tags/tag-api.model';
import { InternalTagRatingTypes } from '@app/tags/tags';
import { UserSearchItemV3Component } from '@app/user/components/user-search-v3/user-search-item-v3/user-search-item-v3.component';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Peer } from '../tag-peer-search/tag-peer-search.component';
import { TagsService } from '@app/tags/services/tags.service';
import { translateWithDefaults } from '@app/shared/utils';

@Component({
    selector: 'dgx-tag-peer-rating-request-modal-v3',
    imports: [
        UserSearchV3Component,
        TextareaComponent,
        UserSearchItemV3Component,
        ReactiveFormsModule,
        AlertComponent,
    ],
    templateUrl: './tag-peer-rating-request-modal-v3.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TagPeerRatingRequestModalV3Component {
  public translate = translateWithDefaults(this.translateService);
  public isRequestProcessing = signal<boolean>(false);
  //holds the selected peers
  public selectedPeers = signal<UserSearchItem[]>([]);
  //holds checkbox value
  public cancelRequests = signal<boolean>(true);
  //computes and returns true if the number of selected peers is greater than the maxPeerCount
  public showPeerCountWarning = computed(
    () => this.peersCount() > this.maxPeerCount
  );
  //holds the number total peers selected (users and groups)
  public peersCount = signal<number>(0);
  //holds the maximum number of characters allowed in the comment textarea
  public commentMaxLength = 1500;
  //holds the maximum number of peers
  public maxPeerCount = 20;
  public comments: FormGroup;
  //holds the manager user profile key
  private managerUserProfilekey = signal<number>(null);
  //holds the organization id
  private orgId = signal<number>(this.authService.authUser.defaultOrgId);
  //computes and returns the user profile keys to exclude
  private userProfileKeysToExclude = computed(() => {
    return this.data.tag?.ratings
      .filter(
        (rating) =>
          rating.type === InternalTagRatingTypes.peer &&
          !rating.dateCompleted &&
          !this.cancelRequests()
      )
      .map((rating) => rating.raterProfileKey);
  });
  //computes and returns the peer profile keys to exclude
  private peerProfileKeysToExclude = computed(() => [
    ...this.userProfileKeysToExclude(),
    this.managerUserProfilekey(),
  ]);

  constructor(
    private translateService: TranslateService,
    private userService: UserService,
    private groupService: GroupService,
    private authService: AuthService,
    private orgMembersService: OrgMembersService,
    private fb: FormBuilder,
    private tagsService: TagsService,
    private dialogService: DialogService,
    private tagModalService: TagModalService,
    @Inject(DIALOG_DATA)
    public data: {
      event: Event;
      tag: TagsApi.TagDetails;
    }
  ) {
    //updates the peers count when the selected peers change
    effect(
      () => {
        this.getPeersCount(this.selectedPeers() as Peer[]).subscribe((count) =>
          this.peersCount.set(count)
        );
      },
      { allowSignalWrites: true }
    );
    this.comments = this.fb.group({
      comment: [''],
    });
  }

  public searchPeers: TypeaheadSearchFunction<string, UserSearchItem> = (
    searchTerm: Observable<string>
  ): Observable<readonly UserSearchItem[]> => {
    return searchTerm.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter((searchTerm) => searchTerm.length >= 2),
      switchMap((searchTerm: string) => this.searchUsersAndGroups(searchTerm)),
      catchError(() => of([]))
    );
  };

  public ngOnInit(): void {
    // Get the manager user profile key to exclude manager from search results
    this.orgMembersService
      .getManager(this.authService.authUser.defaultOrgId)
      .subscribe((manager) => {
        if (manager) {
          this.managerUserProfilekey.set(manager.userProfileKey);
        }
      });
  }

  public submit(): void {
    this.isRequestProcessing.set(true);

    const raterProfileKeys = (this.selectedPeers() as UserProfileSummary[])
      .filter((peer) => !!peer.userProfileKey)
      .map((user) => user.userProfileKey);
    const raterGroupIds = (this.selectedPeers() as unknown as Group[])
      .filter((peer) => !!peer.groupId)
      .map((group) => group.groupId);

    this.tagsService
      .requestTagRatings(
        this.data.event,
        this.data.tag,
        InternalTagRatingTypes.peer,
        raterProfileKeys,
        raterGroupIds,
        this.comments.get('comment').value,
        this.cancelRequests()
      )
      .subscribe({
        next: () => {
          // refresh the list view/skill modal to reflect changes
          this.tagsService.notifyUserTagsModified();
          this.cancel();
        },
        error: (err) => {
          this.isRequestProcessing.set(false);
        },
      });
  }

  public cancel(): void {
    this.tagModalService.openPreviousDialog();
    this.dialogService.close(TagPeerRatingRequestModalV3Component);
  }

  public togglePermission(): void {
    this.cancelRequests.set(!this.cancelRequests());
  }

  public onSelectItem(item: UserSearchItem): void {
    this.selectedPeers.set([...this.selectedPeers(), item]);
  }

  public onRemoveItem(item: UserSearchItem): void {
    this.selectedPeers.set(
      this.selectedPeers().filter(
        (peer) => JSON.stringify(peer) !== JSON.stringify(item)
      )
    );
  }

  private searchUsersAndGroups(
    searchTerm: string
  ): Observable<readonly UserSearchItem[]> {
    const users$ = this.fetchUsers(searchTerm);
    const groups$ = this.fetchGroups(searchTerm);
    //fetch users and groups and filter out the selected peers and manager
    return forkJoin([users$, groups$]).pipe(
      map(([users$, groups$]) => {
        const results = [...users$, ...groups$];
        return results
          .filter(
            (result) =>
              !this.selectedPeers().some(
                (peer) => JSON.stringify(peer) === JSON.stringify(result)
              )
          )
          .filter(
            (result) =>
              !this.peerProfileKeysToExclude().includes(
                (result as UserProfileSummary).userProfileKey
              )
          );
      })
    ) as Observable<readonly UserSearchItem[]>;
  }

  /** Get all users */
  private fetchUsers(searchTerm: string): Observable<UserProfileSummary[]> {
    return this.userService.findNetworkMembers(searchTerm);
  }

  /** Get all groups visible to the organization */
  private fetchGroups(searchTerm: string): Observable<Group[]> {
    return this.groupService.searchGroups(
      this.orgId(),
      GroupPrivacyLevel.Closed,
      searchTerm,
      true // exclude private users on peer group searches
    );
  }

  private getPeersCount(peers: Peer[]): Observable<number> {
    const usersCount = peers.filter(
      (peer) => !!(peer as UserProfileSummary).userProfileKey
    ).length;
    const peerCountObservables = [of(usersCount)];
    peers
      .filter((peer) => !!(peer as Group).groupId)
      .forEach((group) => {
        peerCountObservables.push(
          this.getGroupMemberCount((group as Group).groupId)
        );
      });
    return forkJoin(peerCountObservables).pipe(
      first(),
      map((counts) => counts.reduce((acc, count) => acc + count, 0))
    );
  }

  private getGroupMemberCount(groupId: number): Observable<number> {
    return this.groupService
      .getMemberCount(groupId)
      .pipe(
        map(
          ({ userCount, anonymousUserCount }) => userCount - anonymousUserCount
        )
      );
  }
}
